import React, { useEffect, useState } from 'react';
import { Container, Dropdown, Input, Typeahead } from '../../../components';
import { useCustomer } from '../../../context/customerContext/CustomerContextProvider';
import { ModalProvider } from '../../../components/common/modal/ModalContext';
import { useModal } from '../../../components/common/modal/ModalContext';
import CreateNewCustomer from '../../setting/customer/CreateNewCustomer';
import { useProposal } from '../../../context/proposalContext/ProposalContextProvider';
import { useUser } from '../../../context/userContext/UserContextProvider';
import CustomDateInput from '../../../components/common/inputs/CustomDateInput';
import { addDays, addMonths } from 'date-fns';
import CustomerInfo from './CustomerInfo';
import { useDebouncedCallback } from '@/utils/useDebouncedCallback';

const CustomerSection = () => {
  const {
    getAllCustomer,
    customerData,
    setCustomer,
    searchQuery,
    setSearchQuery,
  } = useCustomer();
  const { getCurrentUser, getSalesReps, salesReps, setSalesReps } = useUser();

  const {
    getSuppliers,
    supplierData,
    proposal,
    setProposal,
    error,
    createNewSupplier,
    setSupplier,
    setError,
  } = useProposal();
  const { openModal, closeModal } = useModal();
  const [supplierQuery, setSupplierQuery] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState([]);
  const [selectedSalesRep, setSelectedSalesRep] = useState([]);
  const [selectedCompeteProposal, setSelectedCompeteProposal] = useState(null);
  const [selectedPriceGstInclude, setSelectedPriceGstInclude] = useState(null);

  useEffect(() => {
    if (searchQuery.length >= 3) {
      getAllCustomer('', ' ', searchQuery);
    }
    return () => {
      // Cleanup function to reset customer data
      setCustomer([]);
    };
  }, [searchQuery]);

  //debouncing
  const debouncedSetSearchQuery = useDebouncedCallback((val) => {
    setSearchQuery(val);
  }, 300);

  const handleSearchChange = (query) => {
    debouncedSetSearchQuery.call(query);
  };

  useEffect(() => {
    getSuppliers();
    getCurrentUser();
    getSalesReps();
    setError(null);
    return () => {
      // Cleanup function to reset suppliers and sales reps
      setSupplier([]);
      setSalesReps([]);
    };
  }, []);

  useEffect(() => {
    if (proposal.currentSupplier?.id) {
      setSelectedSupplier([
        {
          label: proposal.currentSupplier.supplierName,
          value: proposal.currentSupplier.id,
          data: proposal.currentSupplier,
        },
      ]);
    }
    if (
      proposal.customer &&
      (proposal.customer.customerCode || proposal.customer.customerName)
    ) {
      setSelectedCustomer([
        {
          label:
            proposal.customer.customerCode || proposal.customer.customerName
              ? `${proposal.customer.customerCode ?? ''} - ${proposal.customer.customerName ?? ''}`
              : '',
          value: proposal.customer.customerCode ?? '',
          data: proposal.customer,
        },
      ]);
    } else {
      setSelectedCustomer([]);
    }
    if (proposal.salesPerson?.id) {
      setSelectedSalesRep([
        {
          label: `${proposal.salesPerson.salesRep} ${proposal.salesPerson.firstName} ${proposal.salesPerson.lastName}`,
          value: proposal.salesPerson.id,
          data: proposal.salesPerson,
        },
      ]);
    }
    if (proposal.priceGstInclude !== undefined) {
      setSelectedPriceGstInclude(
        proposal.priceGstInclude
          ? { label: 'Inclusive', value: true }
          : { label: 'Exclusive', value: false },
      );
    }
    if (proposal.competeQuote !== undefined) {
      setSelectedCompeteProposal(
        proposal.competeQuote
          ? { label: 'Yes', value: true }
          : { label: 'No', value: false },
      );
    }
    return () => {
      // Cleanup function to reset selected options
      setSelectedSupplier([]);
      setSelectedCustomer([]);
      setSelectedSalesRep([]);
      setSelectedCompeteProposal(null);
      setSelectedPriceGstInclude(null);
    };
  }, [proposal]);

  useEffect(() => {
    if (salesReps.length === 1) {
      const singleSalesRep = salesReps[0];
      setSelectedSalesRep([
        {
          label: `${singleSalesRep.salesRep} ${singleSalesRep.firstName} ${singleSalesRep.lastName}`,
          value: singleSalesRep.id,
          data: singleSalesRep,
        },
      ]);
      setProposal((prev) => ({
        ...prev,
        salesPerson: singleSalesRep,
      }));
    } else if (proposal.salesPerson?.id) {
      const existingSalesRep = salesReps.find(
        (rep) => rep.id === proposal.salesPerson.id,
      );
      if (existingSalesRep) {
        setSelectedSalesRep([
          {
            label: `${existingSalesRep.salesRep} ${existingSalesRep.firstName} ${existingSalesRep.lastName}`,
            value: existingSalesRep.id,
            data: existingSalesRep,
          },
        ]);
      }
    }
    return () => {
      // Cleanup function to reset selected sales rep
      setSelectedSalesRep([]);
    };
  }, [salesReps, proposal.salesPerson]);

  const customerOptions = customerData?.map((item) => {
    return {
      label:
        item.customerCode || item.customerName
          ? `${item.customerCode ?? ''} - ${item.customerName ?? ''}`
          : '',
      value: item.customerCode ?? '',
      data: item, // Store the entire customer object
    };
  });

  const supplierOptions = supplierData?.map((item) => ({
    label: item.supplierName,
    value: item.id,
    data: item, // Store the entire supplier object
  }));

  const salesRepOptions = salesReps?.map((rep) => ({
    label: `${rep.salesRep} ${rep.firstName} ${rep.lastName}`,
    value: rep.id,
    data: rep, // Store the entire sales rep object
  }));

  const handleOpenModal = (option) => {
    setCustomer((prev) => ({
      ...prev,
      id: 0,
      customerCode: option,
    }));
    openModal(<CreateNewCustomer closeModal={closeModal} />, 'Create Customer');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProposal((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCustomerSelect = (selected) => {
    if (selected.length > 0) {
      const selectedCustomer = selected[0].data;
      setSelectedCustomer(selected);
      setProposal((prev) => ({
        ...prev,
        customer: selectedCustomer,
      }));
    }
  };

  const handleTypeaheadChange =
    (field, setSelected, setQuery) => (selectedOptions) => {
      const selectedOption = selectedOptions.length ? selectedOptions[0] : null;

      if (selectedOption) {
        setSelected(selectedOptions);
        setProposal((prev) => ({
          ...prev,
          [field]: {
            id: selectedOption.value,
            [`${field}Name`]: selectedOption.label,
            ...selectedOption.data, // Make sure to persist the full data object
          },
        }));
        setQuery(selectedOption.label); // Update the query with the selected option's label
      } else {
        setSelected([]); // Clear selection if no option is selected
        setProposal((prev) => ({ ...prev, [field]: null }));
        setQuery('');
      }
    };

  const handleTypeaheadBlur =
    (field, setSelected, setQuery, query, response) => (e) => {
      if (!response.some((option) => option.label === query)) {
        setSelected([]);
        setQuery('');
        setProposal((prev) => ({ ...prev, [field]: null }));
      }
    };

  const handleSalesRepSelect = (e) => {
    const selectedValue = e.target.value;
    const selectedSalesRep = salesReps.find(
      (rep) => rep.id === parseInt(selectedValue),
    );

    if (selectedSalesRep) {
      setSelectedSalesRep([
        {
          label: `${selectedSalesRep.salesRep} ${selectedSalesRep.firstName} ${selectedSalesRep.lastName}`,
          value: selectedSalesRep.id,
          data: selectedSalesRep,
        },
      ]);
      setProposal((prev) => ({
        ...prev,
        salesPerson: selectedSalesRep,
      }));
    } else {
      // console.log("Sales Rep not found for the selected ID.");
    }
  };

  const today = new Date();
  const closeDate = addDays(today, 1);

  const createSupplier = (query) => {
    setSupplier((prev) => ({
      ...prev,
      supplierName: query,
    }));
    createNewSupplier({ supplierName: query });
  };

  return (
    <div className="flex items-center mt-10 min-h-full">
      <div className="basis-3/4">
        <Container title="Customer ">
          <div className="lg:grid grid-cols-2 gap-2 items-center p-5">
            <div>
              <Typeahead
                required
                disabled={!!proposal.id}
                onQueryChange={handleSearchChange}
                label="Customer Code"
                name="customer"
                placeholder="Choose Customer Code"
                options={customerOptions}
                labelKey="label"
                handleCreateNew={handleOpenModal}
                onChange={handleCustomerSelect}
                selected={selectedCustomer}
                custErr={error?.customer}
              />
            </div>
            <div>
              <Input
                name="quoteAttn"
                type="text"
                label="Proposal Attn"
                placeholder="Enter Proposal Attn"
                value={proposal.quoteAttn}
                onChange={handleInputChange}
                required
                error={error?.quoteAttn}
              />
            </div>
            <div>
              <Typeahead
                required
                label="Current Supplier"
                name="currentSupplier"
                placeholder="Choose Supplier"
                options={supplierOptions}
                labelKey="label"
                createNewLabel="Create new"
                handleCreateNew={createSupplier}
                onChange={handleTypeaheadChange(
                  'currentSupplier',
                  setSelectedSupplier,
                  setSupplierQuery,
                )}
                onBlur={handleTypeaheadBlur(
                  'currentSupplier',
                  setSelectedSupplier,
                  setSupplierQuery,
                  supplierQuery,
                  supplierOptions,
                )}
                selected={selectedSupplier}
                custErr={error?.currentSupplier}
              />
            </div>
            <div>
              <Dropdown
                required
                name="salesPerson"
                label="Sales Person"
                options={salesRepOptions}
                onChange={handleSalesRepSelect}
                value={selectedSalesRep.length ? selectedSalesRep[0].value : ''}
                error={error?.salesPerson}
              />
            </div>
            <div>
              <CustomDateInput
                label="Created Date"
                error={error?.createdDate}
                value={proposal.createdDate}
                onChange={(formattedDate) =>
                  setProposal((prev) => ({
                    ...prev,
                    createdDate: formattedDate,
                  }))
                }
                maxDate={today} // Ensure created date is not in the future
              />
            </div>
            <div>
              <Dropdown
                required
                name="competeQuote"
                label="Compete Proposal"
                options={[
                  { label: 'Yes', value: true },
                  { label: 'No', value: false },
                ]}
                onChange={(e) => {
                  const selectedValue = e.target.value === 'true'; // Convert string to boolean
                  setSelectedCompeteProposal(
                    selectedValue
                      ? { label: 'Yes', value: true }
                      : { label: 'No', value: false },
                  );
                  setProposal((prev) => ({
                    ...prev,
                    competeQuote: selectedValue,
                  }));
                }}
                value={
                  selectedCompeteProposal ? selectedCompeteProposal.value : ''
                }
                error={error?.competeQuote}
              />
            </div>
            <div>
              <CustomDateInput
                label="Close Date"
                error={error?.closeDate}
                value={proposal.closeDate}
                onChange={(formattedDate) =>
                  setProposal((prev) => ({
                    ...prev,
                    closeDate: formattedDate,
                  }))
                }
                minDate={closeDate} // Ensure close date is not in the past
              />
            </div>
            <div>
              <Dropdown
                required
                name="priceGstInclude"
                label="Price GST"
                options={[
                  { label: 'Inclusive', value: true },
                  { label: 'Exclusive', value: false },
                ]}
                onChange={(e) => {
                  const selectedValue = e.target.value === 'true'; // Convert string to boolean
                  setSelectedPriceGstInclude(
                    selectedValue
                      ? { label: 'Inclusive', value: true }
                      : { label: 'Exclusive', value: false },
                  );
                  setProposal((prev) => ({
                    ...prev,
                    priceGstInclude: selectedValue,
                  }));
                }}
                value={
                  selectedPriceGstInclude ? selectedPriceGstInclude.value : ''
                }
                error={error?.priceGstInclude}
              />
            </div>
          </div>
        </Container>
      </div>

      {/* Customer information display */}
      <CustomerInfo proposal={proposal} />
    </div>
  );
};

const App = () => (
  <ModalProvider>
    <CustomerSection />
  </ModalProvider>
);

export default App;
